/**
 * chartUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared display utilities for MarketPulze charts.
 *
 * Exports:
 *   fmtPrice(v)        — format a number as a USD price string
 *   fmtVol(v)          — format volume with K/M/B suffix
 *   getMarketStatus()  — current US market session: "open"|"pre"|"after"|"closed"
 *
 * Why extracted from calcIndicators.js:
 *   These are display helpers and a runtime status check — not indicator math.
 *   calcIndicators.js is a pure maths file. Keeping non-math utilities there
 *   muddies the single-responsibility principle and creates confusing imports
 *   (chartRenderers.js importing from a "calc" file for display formatting).
 *
 *   calcIndicators.js re-exports these for backwards compatibility so existing
 *   imports continue to work without changes.
 *
 * Dependencies: none — no React, no D3, no other internal modules
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * fmtPrice — Format a number as a USD price string
 * Handles null/undefined gracefully with "—" fallback.
 * Uses toLocaleString for thousands separators (e.g. $1,234.56).
 *
 * @param {number|null} v
 * @returns {string}
 */
export function fmtPrice(v) {
  return v == null ? '—' : '$' + Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * fmtVol — Format a volume number with K/M/B suffix
 * e.g. 1234567 → "1.2M", 43200000 → "43.2M"
 *
 * @param {number|null} v
 * @returns {string}
 */
export function fmtVol(v) {
  if (!v) return '—';
  if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(0) + 'K';
  return String(v);
}

/**
 * getMarketStatus — Current US market session status
 * Returns: "open" | "pre" | "after" | "closed"
 *
 * Uses browser's Intl API to get current ET time.
 * Used to show the WebSocket live dot and extended hours context.
 * Also included in every D3 signal log payload (sessionHour + marketStatus).
 *
 * Session boundaries (ET):
 *   Pre-market:  04:00 – 09:29
 *   Regular:     09:30 – 15:59
 *   After-hours: 16:00 – 19:59
 *   Closed:      20:00 – 03:59 + weekends
 *
 * @returns {"open"|"pre"|"after"|"closed"}
 */
export function getMarketStatus() {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const m  = et.getHours() * 60 + et.getMinutes();
  const d  = et.getDay();
  if (d === 0 || d === 6 || m < 240)  return 'closed';
  if (m < 570)                         return 'pre';
  if (m < 960)                         return 'open';
  if (m < 1200)                        return 'after';
  return 'closed';
}
